import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  TextField,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ToastContext } from "../utils/ToastContext";
import {
  createSMSTemplate,
  getSMSProviderMaster,
} from "../Services/templatesService/SMSTemplateService";
import TemplateProviderDropdown from "./TemplateProviderDropdown";

const initialFormState = {
  smstemplatename: "",
  smstemplatetext: "",
  isactive: true,
  smsproviderid: "",
  smstemplateid: "",
  tracklinkid: "",
};

const CommunicationTemplateFormDrawer = ({
  open,
  onClose,
  onSave,
  onSuccess,
  mode = "create",
  initialData = null,
}) => {
  const [formState, setFormState] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [smsProviders, setSmsProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [errors, setErrors] = useState({});
  const { showToast } = useContext(ToastContext);

  useEffect(() => {
    if (!open) return;

    fetchSMSProviders();
    setErrors({});

    if (mode === "clone" && initialData) {
      setFormState({
        ...initialFormState,
        smstemplatename: initialData.templateName ?? "",
        smstemplatetext: initialData.templateContent ?? "",
        isactive: true, // Set to true for cloned templates
        smsproviderid: initialData.providerId ?? "",
        smstemplateid: initialData.templateId ?? "",
        tracklinkid: initialData.trackLinkId ?? "",
      });
    } else {
      setFormState(initialFormState);
    }
  }, [open, mode, initialData]);

  const fetchSMSProviders = async () => {
    setLoadingProviders(true);
    try {
      const response = await getSMSProviderMaster();
      if (response?.data?.SmsProviderMaster) {
        const transformedProviders = response.data.SmsProviderMaster.map(
          (provider) => ({
            id: provider.id,
            name: provider.smsprovidername,
          }),
        );
        setSmsProviders(transformedProviders);
      }
    } catch (error) {
      console.error("Error fetching SMS providers:", error);
      showToast("Failed to load SMS providers", "error");
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    // Clear error for the field being edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleProviderChange = (provider) => {
    setFormState((prev) => ({
      ...prev,
      smsproviderid: provider.id,
    }));
    // Clear error for smsproviderid
    if (errors.smsproviderid) {
      setErrors((prev) => ({ ...prev, smsproviderid: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formState.smstemplatename.trim()) {
      newErrors.smstemplatename = "Template Name is required.";
    } else if (formState.smstemplatename.length > 256) {
      newErrors.smstemplatename =
        "Template Name must be 256 characters or less.";
    }

    if (!formState.smstemplatetext.trim()) {
      newErrors.smstemplatetext = "Template Content is required.";
    } else if (formState.smstemplatetext.length > 2000) {
      newErrors.smstemplatetext =
        "Template Content must be 2000 characters or less.";
    }

    if (!formState.smsproviderid) {
      newErrors.smsproviderid = "SMS Provider is required.";
    }

    const alphanumericRegex = /^[a-zA-Z0-9]*$/;

    if (!formState.smstemplateid.trim()) {
      newErrors.smstemplateid = "SMS Template ID is required.";
    } else if (formState.smstemplateid.length > 50) {
      newErrors.smstemplateid =
        "SMS Template ID must be 50 characters or less.";
    } else if (!alphanumericRegex.test(formState.smstemplateid)) {
      newErrors.smstemplateid =
        "SMS Template ID must contain only alphanumeric characters.";
    }

    if (!formState.tracklinkid.trim()) {
      newErrors.tracklinkid = "Track Link ID is required.";
    } else if (formState.tracklinkid.length > 20) {
      newErrors.tracklinkid = "Track Link ID must be 20 characters or less.";
    } else if (!alphanumericRegex.test(formState.tracklinkid)) {
      newErrors.tracklinkid =
        "Track Link ID must contain only alphanumeric characters.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await createSMSTemplate(formState);
      showToast("SMS Template created successfully", "success");
      onClose();
      onSuccess?.();
    } catch (error) {
      showToast(
        error?.response?.data?.error?.message || "Failed to create template",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = (event) => {
    setFormState((prev) => ({ ...prev, isactive: event.target.checked }));
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: { xs: "100vw", sm: 480 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: 3,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6">
            {mode === "clone"
              ? "Clone Communication Template"
              : "Add Communication Template"}
          </Typography>
          <IconButton onClick={onClose} aria-label="close drawer">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
          <TextField
            label="Template Name"
            name="smstemplatename"
            value={formState.smstemplatename}
            onChange={handleChange}
            fullWidth
            error={!!errors.smstemplatename}
            helperText={errors.smstemplatename}
          />

          <TextField
            label="Template Content"
            name="smstemplatetext"
            value={formState.smstemplatetext}
            onChange={handleChange}
            fullWidth
            multiline
            rows={6}
            error={!!errors.smstemplatetext}
            helperText={errors.smstemplatetext}
          />

          {/* <FormControlLabel
            control={
              <Switch
                checked={formState.isactive}
                onChange={handleToggleActive}
                color="primary"
              />
            }
            label={formState.isactive ? "Active: Yes" : "Active: No"}
          /> */}

          <FormControlLabel
            sx={{
              "& .MuiSwitch-switchBase.Mui-disabled": {
                color: "#9e9e9e",
              },
              "& .MuiSwitch-track": {
                backgroundColor: "#cfcfcf !important",
              },
            }}
            control={<Switch checked={formState.isactive} disabled />}
            label={formState.isactive ? "Active: Yes" : "Active: No"}
          />

          <TemplateProviderDropdown
            label="SMS Provider"
            placeholder="Select SMS Provider"
            options={smsProviders}
            value={
              smsProviders.find(
                (provider) => provider.id === formState.smsproviderid,
              ) || null
            }
            onChange={handleProviderChange}
            onClear={() =>
              setFormState((prev) => ({ ...prev, smsproviderid: "" }))
            }
            disabled={loadingProviders}
          />
          {errors.smsproviderid && (
            <Typography variant="body2" color="error">
              {errors.smsproviderid}
            </Typography>
          )}

          <TextField
            label="SMS Template ID"
            name="smstemplateid"
            value={formState.smstemplateid}
            onChange={handleChange}
            fullWidth
            error={!!errors.smstemplateid}
            helperText={errors.smstemplateid}
          />

          <TextField
            label="Track Link ID"
            name="tracklinkid"
            value={formState.tracklinkid}
            onChange={handleChange}
            fullWidth
            error={!!errors.tracklinkid}
            helperText={errors.tracklinkid}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? "Saving..." : mode === "clone" ? "Clone" : "Save"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CommunicationTemplateFormDrawer;
